/**
 * Spanish message catalog — mirrors the shape of `messages.en.ts`
 * exactly. The shape is enforced structurally via `satisfies` against
 * a widened (non-`as const`) view of the EN catalog so a new key in
 * EN that doesn't land here is a TypeScript error.
 *
 * `satisfies` (rather than `: MessagesShape =`) preserves the literal
 * Spanish strings on the exported object so future tooling — testing
 * against translation keys, generating PO files — can read the exact
 * Spanish text rather than the widened `string` type.
 *
 * Wired in `index.ts` once a runtime locale picker exists. For now
 * the file lives standalone so the EN catalog still drives all
 * runtime reads.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Conectado",
    statusReconnecting: "Reconectando…",
    statusDisconnected: "Desconectado",
    openSettings: "Abrir configuración",
    pairedFallbackDevice: "tu teléfono",
    publicModeToggleEnable: "Ocultar contenido de notificaciones",
    publicModeToggleDisable: "Mostrar contenido de notificaciones",
  },

  notifications: {
    replyAriaLabel: "Responder",
    replyPlaceholder: "Responder…",
    replySend: "Enviar",
    sent: "Enviado",
    failedToSend: "No se pudo enviar",
    unread: "Sin leer",
    dismiss: "Descartar",
    notificationImageAlt: "Imagen de notificación",
  },

  conversations: {
    replyAriaLabel: "Responder",
    replyPlaceholder: "Responder…",
    replySend: "Enviar",
    sent: "Enviado",
    failedToSend: "No se pudo enviar",
    dismiss: "Descartar conversación",
    messagesListLabel: "Mensajes en esta conversación",
    deviceOwner: "Tú",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Sin mensajes nuevos"
        : `${count} ${selectPlural("es", count, {
            one: "mensaje sin leer",
            other: "mensajes sin leer",
          })}`,
  },

  list: {
    today: "Hoy",
    yesterday: "Ayer",
    searchNotifications: "Buscar notificaciones",
    clearSearch: "Borrar búsqueda",
    clearAll: "Borrar todo",
    markAllRead: "Marcar todo como leído",
    groupCheckboxLabel: "Agrupar",
    noNotificationsTitle: "Aún no hay notificaciones",
    noNotificationsDesc: "Las notificaciones de tu teléfono aparecerán aquí.",
    noMatchesTitle: "Sin coincidencias",
    notificationsList: "Notificaciones",
    privacyAppList: "Notificaciones por aplicación",
    clear: "Borrar",
    clearMatches: (count: number): string =>
      `Borrar ${count} ${selectPlural("es", count, {
        one: "coincidencia",
        other: "coincidencias",
      })}`,
    noMatchesDesc: (query: string): string => `Ninguna notificación coincide con “${query}”.`,
    appNotificationsGroup: (appName: string): string => `Notificaciones de ${appName}`,
    dismissAllFrom: (appName: string): string => `Descartar todas de ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("es", count, { one: "mensaje", other: "mensajes" })
          : kind === "calls"
            ? selectPlural("es", count, { one: "llamada", other: "llamadas" })
            : selectPlural("es", count, { one: "notificación", other: "notificaciones" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "descartar",
    kbdNavigate: "navegar",
    kbdOrSeparator: "o",
  },

  settings: {
    back: "Volver a notificaciones",
    settings: "Configuración",
    appearance: "Apariencia",
    theme: "Tema",
    themeAuto: "Automático",
    themeLight: "Claro",
    themeDark: "Oscuro",
    language: "Idioma",
    languageSystemDefault: "Predeterminado del sistema",
    notificationsSection: "Notificaciones",
    osNotifications: "Notificaciones del sistema",
    callNotifications: "Notificaciones de llamadas",
    privacySection: "Privacidad",
    analyticsToggle: "Estadísticas de uso anónimas",
    analyticsToggleDescription: "Solo recuentos e hitos — nunca tus notificaciones.",
    connection: "Conexión",
    status: "Estado",
    statusConnected: "Conectado",
    statusReconnecting: "Reconectando…",
    statusDisconnected: "Desconectado",
    disconnectFromPhone: "Desconectar del teléfono",
    disconnectConfirm: "¿Estás seguro? Se desvinculará tu dispositivo.",
    yesDisconnect: "Sí, desconectar",
    cancel: "Cancelar",
    aboutAndSupport: "Acerca de y soporte",
    version: "Versión",
    encryption: "Cifrado",
    encryptionValue: "E2E (NaCl)",
    needHelp: "¿Necesitas ayuda? Escribe a __EMAIL__",
    disconnectFailed: "No se pudo desconectar — vuelve a intentarlo",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Desconectado, pero la limpieza local falló: ${errorMsg}. Vuelve a cargar la página.`,
    disconnectLocalCleanupFailed:
      "Desconectado, pero la limpieza local falló. Vuelve a cargar la página.",
  },

  pairing: {
    enterCodeInstruction: "Introduce este código en la app Reflecto",
    pairedSuccessfully: "¡Vinculación correcta!",
    pairingFailed: "Vinculación fallida",
    requestingPairingCode: "Solicitando código de vinculación…",
    codeExpired: "Código caducado",
    tryAgain: "Reintentar",
    failedToStartPairing: "No se pudo iniciar la vinculación",
    needAppText: "¿Necesitas la app de Android? Escribe a __EMAIL__ para obtener acceso.",
  },

  dnd: {
    title: "No molestar",
    description: "Pausa las notificaciones en este dispositivo durante una hora.",
    snooze1h: "Posponer 1 hora",
    snoozeActiveUntil: (formattedTime: string): string => `Activo hasta ${formattedTime}`,
    cancelSnooze: "Cancelar",
  },

  connectors: {
    title: "Servicios conectados",
    emptyBody:
      "Aún no hay servicios conectados. Conecta ChatGPT o Claude desde su configuración de conectores y luego aprueba el código en tu teléfono.",
    scopeSend: "Puede enviar notificaciones",
    added: (date: string): string => `Añadido ${date}`,
    lastUsed: (relative: string): string => `Usado por última vez ${relative}`,
    neverUsed: "Nunca usado",
    revoke: "Desconectar",
    revokeConfirm: (label: string): string =>
      `¿Desconectar ${label}? Perderá el acceso de inmediato.`,
    revokeAction: "Desconectar",
    revokeConfirmLabel: (label: string): string => `Desconectar ${label}`,
    cancel: "Cancelar",
    loadError: "No se pudieron cargar los servicios conectados. Inténtalo de nuevo.",
    revokeError: "No se pudo desconectar el servicio. Inténtalo de nuevo.",
    retry: "Reintentar",
  },

  feedback: {
    title: "Enviar comentarios",
    close: "Cerrar",
    messageLabel: "Tu mensaje",
    messagePlaceholder: "¿Encontraste un error, tienes una idea o algo no te cuadra? Cuéntanos.",
    categoryLabel: "Categoría",
    categoryBug: "Error",
    categoryIdea: "Idea",
    categoryOther: "Otro",
    emailLabel: "Correo (opcional, solo si quieres una respuesta)",
    emailPlaceholder: "tu@ejemplo.com",
    emailHelp: "No te añadiremos a ninguna lista.",
    disclosure:
      "Tu mensaje se envía al servidor de Reflecto y se registra en nuestro sistema interno de seguimiento de incidencias. Adjuntamos la versión de la app y un ID de dispositivo asignado durante el emparejamiento para poder correlacionar los reportes. No vemos tus notificaciones, contactos ni ningún dato personal. El correo es opcional — facilítalo solo si quieres una respuesta. No te añadimos a ninguna lista.",
    submit: "Enviar comentarios",
    sending: "Enviando…",
    success: "Gracias — lo lee una persona, normalmente en menos de 24 h",
    error: "No se pudo enviar. Inténtalo de nuevo.",
    done: "Listo",
    // Etiqueta del punto de entrada en Ajustes (abre el modal). Idéntica a propósito
    // a `submit` (el botón del modal) — claves distintas para que cada idioma pueda divergir.
    sendFeedback: "Enviar comentarios",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Cerrar",
    aiSummaryTag: "Resumen con IA",
    errorAi: "No se pudo generar un resumen en este momento.",
    retryCta: "Reintentar",
    // Tarjeta de incorporación cuando el modelo en el dispositivo se puede descargar.
    downloadHeading: "Obtén resúmenes con IA",
    downloadBody:
      "Ponte al día con lo que te perdiste con un resumen de IA en el dispositivo. Se ejecuta totalmente en tu dispositivo.",
    downloadCta: "Descargar modelo",
    downloadPrivacyNote: "Una descarga única. El servidor nunca ve tus notificaciones.",
    downloadingHeading: "Descargando modelo…",
    caughtUp: "Estás al día: nada requiere tu atención ahora mismo.",
    needsYou: "Te necesita",
    quiet: "Tranquilo",
    scanning: (pending: number): string => `Resumiendo ${pending}…`,
    jump: "Abrir notificación",
    affordanceReply: "Responder",
    affordanceOpen: "Abrir",
    affordanceCopyOtp: "Copiar código",
    buttonLabel: "Glance",
  },

  update: {
    title: "Actualización disponible",
    description: "Una nueva versión de Reflecto está lista. Recarga para actualizar.",
    reload: "Recargar",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Llamamos a este dispositivo “${name}”.`,
    cardLooksGood: "Está bien",
    cardEdit: "Editar",
    cardAriaLabel: "Confirmar nombre del dispositivo",
    renameTitle: "Cambiar nombre del dispositivo",
    renameLabel: "Nombre del dispositivo",
    renameSave: "Guardar",
    renameCancel: "Cancelar",
    renameFailed: "No se pudo renombrar. Inténtalo de nuevo.",
  },

  collision: {
    title: (name: string): string => `Ya tienes un “${name}”`,
    subtitle: "¿Cómo deberíamos llamar a este?",
    chipWork: (name: string): string => `${name} del trabajo`,
    chipHome: (name: string): string => `${name} del hogar`,
    chipPersonal: (name: string): string => `${name} personal`,
    customLabel: "Nombre personalizado",
    save: "Guardar",
    useAnyway: "Usar el nombre original de todos modos",
    saveFailed: "No se pudo guardar el nuevo nombre. Inténtalo de nuevo.",
    ariaLabel: "Resolver nombre duplicado del dispositivo",
  },

  clipboard: {
    title: "Portapapeles",
    empty: "Los clips de tus otros dispositivos aparecen aquí. Toca para copiar.",
    from: (label: string): string => `de ${label}`,
    copy: "Copiar al portapapeles",
    copied: "Copiado",
    reveal: "Mostrar",
    hide: "Ocultar",
    maskedHint: "Contenido oculto: pulsa Mostrar para verlo",
    sensitiveChip: "Sensible",
    cloudChip: "desde tu token de API",
    sendPlaceholder: "Pega texto para enviar…",
    sendCta: "Enviar",
    sendSensitiveToggle: "Marcar como sensible",
    sendSuccess: (n: number): string =>
      `Enviado a ${n} ${selectPlural("es", n, { one: "dispositivo", other: "dispositivos" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Llegó a ${n}/${total} ${selectPlural("es", total, {
        one: "dispositivo",
        other: "dispositivos",
      })}.`,
    sendNoDevices: "No hay dispositivos emparejados para enviar.",
    sendFailed: "No se pudo enviar. Inténtalo de nuevo.",
    sendEmpty: "Nada que enviar.",
    dockExpand: "Mostrar historial del portapapeles",
    dockCollapse: "Contraer",
    dockSendTitle: "Enviar a tus dispositivos",
    dockFromClipboard: (chars: number): string =>
      `Del portapapeles · ${chars} ${selectPlural("es", chars, {
        one: "carácter",
        other: "caracteres",
      })}`,
    dockSend: (n: number): string => `Enviar a ${n}`,
    dockCancel: "Cancelar",
    dockRecentHeading: "Recientes",
    dockEmptyClipboard: "Tu portapapeles está vacío.",
    shareTitle: "Enviar a tus dispositivos",
    shareReview:
      "Revisa lo que vas a enviar. Va a tus dispositivos emparejados, cifrado de extremo a extremo.",
    shareSend: "Enviar",
    shareCancel: "Cancelar",
    shareNotPaired: "Empareja un dispositivo primero para enviar texto compartido.",
    shareOpenApp: "Abrir Reflecto",
  },
  install: {
    title: "Instalar Reflecto",
    body: "Agrégalo a tu pantalla de inicio para acceder más rápido.",
    cta: "Instalar",
    dismissAria: "Descartar el aviso de instalación",
  },
  common: {
    loading: "Cargando",
  },
  errors: {
    notPaired: "Sin vincular",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} en ${os}`,
    webFallback: "Navegador web",
  },
  palette: {
    placeholder: "Escribe un comando…",
    noResults: "No hay comandos coincidentes",
    ariaLabel: "Paleta de comandos",
    clearAll: "Borrar todas las notificaciones",
    glance: "Resumen de Glance",
    cancelSnooze: "Cancelar pausa",
    copyClip: "Copiar el último clip recibido",
  },
} satisfies MessagesShape;
